import React, { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "./ui/dialog";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import {
  Package,
  Upload,
  Plus,
  Trash,
  ArrowClockwise,
  Envelope,
  Warning,
  CheckCircle,
  Clock,
  ClipboardText,
  FileArrowUp,
  CaretUp,
  CaretDown,
  PencilSimple,
  ArrowSquareOut,
  MagnifyingGlass,
  Star,
  Check as CheckIcon,
  X as XIcon,
} from "@phosphor-icons/react";
import {
  fetchTrackedLinks,
  addTrackedLink,
  deleteTrackedLink,
  bulkDeleteTrackedLinks,
  updateTrackedLink,
  checkSingleLink,
  uploadTrackerExcel,
  trackerCheckNow,
  fetchTrackerHistory,
  fetchTrackerSettings,
  updateTrackerSettings,
  fetchTrackerLastRun,
  telegramDetect,
  setTelegramChatId,
  testTelegram,
  sendTelegramReport,
} from "../lib/api";
import { formatApiError } from "../lib/auth";
import { toast } from "sonner";
import { TrackerDiagnostics } from "./TrackerDiagnostics";
import { CmtExportButtons } from "./CmtExportButtons";

const CUBAMAX_EVENTS = [
  "CREATED",
  "CHECK-IN",
  "SHIPPED",
  "ARRIVED",
  "READY FOR DISTRIBUTION",
  "DELIVERED",
];

const timeAgo = (iso) => {
  if (!iso) return "never";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
};

export const TrackerTab = () => {
  const [links, setLinks] = useState([]);
  const [settings, setSettings] = useState(null);
  const [lastRun, setLastRun] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [newUrl, setNewUrl] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [emailInput, setEmailInput] = useState("");
  const [telegramChatId, setTelegramChatIdState] = useState("");
  const [telegramChats, setTelegramChats] = useState([]);
  const [detectingTelegram, setDetectingTelegram] = useState(false);
  const [sendingReport, setSendingReport] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortKey, setSortKey] = useState("added");
  const [sortDir, setSortDir] = useState("desc");
  const [editingId, setEditingId] = useState(null);
  const [editLabel, setEditLabel] = useState("");
  const [checkingId, setCheckingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const searchInputRef = useRef(null);
  const fileInputRef = useRef(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [l, s, r, h] = await Promise.all([
        fetchTrackedLinks(),
        fetchTrackerSettings(),
        fetchTrackerLastRun(),
        fetchTrackerHistory(null, 30),
      ]);
      setLinks(l);
      setSettings(s);
      setLastRun(r);
      setHistory(h);
      setEmailInput(s.notify_email || "");
      setTelegramChatIdState(s.telegram_chat_id || "");
    } catch (e) {
      toast.error("Failed to load tracker data");
    }
    setLoading(false);
  };

  useEffect(() => {
    loadAll();
    // Poll every 30s in case tracker fires in background
    const t = setInterval(loadAll, 30000);
    return () => clearInterval(t);
  }, []);

  const handleAddLink = async () => {
    if (!newUrl.trim()) return;
    try {
      await addTrackedLink(newUrl.trim(), newLabel.trim());
      toast.success("Link added");
      setNewUrl("");
      setNewLabel("");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Remove this tracked link?")) return;
    try {
      await deleteTrackedLink(id);
      toast.success("Removed");
      loadAll();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Borrar ${ids.length} tracking${ids.length > 1 ? "s" : ""} seleccionado${ids.length > 1 ? "s" : ""}?`)) return;
    try {
      const res = await bulkDeleteTrackedLinks(ids);
      toast.success(`${res.deleted} eliminados`);
      setSelectedIds(new Set());
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Bulk delete failed");
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = (rows) => {
    setSelectedIds((prev) => {
      if (prev.size === rows.length) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const startEdit = (link) => {
    setEditingId(link.id);
    setEditLabel(link.label || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditLabel("");
  };

  const saveEdit = async (id) => {
    try {
      await updateTrackedLink(id, { label: editLabel });
      toast.success("Nota actualizada");
      setEditingId(null);
      setEditLabel("");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const handleCheckOne = async (id) => {
    setCheckingId(id);
    try {
      const res = await checkSingleLink(id);
      const changes = res?.result?.changes?.length || 0;
      const err = res?.result?.error;
      if (err) toast.error(err);
      else if (changes) toast.success(`✓ ${changes} cambio${changes > 1 ? "s" : ""} detectado${changes > 1 ? "s" : ""}`);
      else toast.success("✓ Revisado, sin cambios");
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Check failed");
    } finally {
      setCheckingId(null);
    }
  };

  const toggleVip = async (link) => {
    try {
      await updateTrackedLink(link.id, { is_vip: !link.is_vip });
      toast.success(
        !link.is_vip
          ? "⭐ Marcado VIP — revisión cada 5 min"
          : "Quitado de VIP",
      );
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Update failed");
    }
  };

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const res = await uploadTrackerExcel(file);
      toast.success(
        `Excel processed: ${res.inserted} new, ${res.skipped} duplicates`,
      );
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || "Upload failed");
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await trackerCheckNow();
      toast.success(
        `Checked ${res.checked} links: ${res.with_changes} with changes, ${res.emails_sent} emails sent`,
      );
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Check failed");
    } finally {
      setChecking(false);
    }
  };

  const handleSaveEmail = async () => {
    try {
      await updateTrackerSettings(emailInput);
      toast.success("Notification email updated");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleDetectTelegram = async () => {
    setDetectingTelegram(true);
    try {
      const res = await telegramDetect();
      setTelegramChats(res.chats || []);
      if ((res.chats || []).length === 0) {
        toast.warning(
          "No hay mensajes recientes. Envía /start a tu bot desde Telegram y vuelve a intentar.",
        );
      } else {
        toast.success(`Se encontraron ${res.chats.length} chat(s)`);
      }
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally {
      setDetectingTelegram(false);
    }
  };

  const handlePickChat = async (chatId) => {
    try {
      await setTelegramChatId(chatId);
      setTelegramChatIdState(chatId);
      toast.success(`Chat ID ${chatId} guardado`);
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleSaveChatId = async () => {
    if (!telegramChatId.trim()) return;
    try {
      await setTelegramChatId(telegramChatId.trim());
      toast.success("Chat ID saved");
      loadAll();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const handleTestTelegram = async () => {
    try {
      await testTelegram();
      toast.success("Mensaje de prueba enviado a Telegram ✓");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    }
  };

  const handleSendReport = async (forceRefresh) => {
    setSendingReport(true);
    try {
      const res = await sendTelegramReport(forceRefresh);
      toast.success(
        forceRefresh
          ? `Reporte enviado: ${res.checked} revisados, ${res.changes_found} con cambios (${res.messages_sent} msg)`
          : `Reporte enviado a Telegram (${res.messages_sent} mensaje${res.messages_sent > 1 ? "s" : ""})`,
      );
      loadAll();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed");
    } finally {
      setSendingReport(false);
    }
  };

  const openHistory = async (link) => {
    setSelectedLink(link);
    setHistoryOpen(true);
    try {
      const h = await fetchTrackerHistory(link.id, 50);
      setHistory(h);
    } catch {
      toast.error("Failed to load history");
    }
  };

  const renderEventBadge = (link, evName) => {
    const ev = link.current_snapshot?.events?.[evName];
    if (!ev)
      return (
        <span className="font-mono text-[9px] px-1.5 py-0.5 bg-zinc-100 text-zinc-400 uppercase tracking-wider">
          —
        </span>
      );
    const done = ev.status === "completed";
    const tooltip = [ev.date, ev.description].filter(Boolean).join(" · ");
    return (
      <span
        className={`font-mono text-[9px] px-1.5 py-0.5 uppercase tracking-wider ${
          done
            ? "bg-green-100 text-green-800 border border-green-300"
            : "bg-zinc-100 text-zinc-500"
        }`}
        title={tooltip}
      >
        {done ? "✓" : "…"}
      </span>
    );
  };

  const currentStep = (link) => {
    const events = link.current_snapshot?.events || {};
    // Latest completed
    let latest = null;
    for (const ev of CUBAMAX_EVENTS) {
      if (events[ev]?.status === "completed") latest = ev;
    }
    // Next pending
    let next = null;
    for (const ev of CUBAMAX_EVENTS) {
      if (events[ev]?.status === "pending") {
        next = ev;
        break;
      }
    }
    return { latest, next, events };
  };

  const latestCompletedIdx = (link) => {
    const events = link.current_snapshot?.events || {};
    let idx = -1;
    for (let i = 0; i < CUBAMAX_EVENTS.length; i++) {
      if (events[CUBAMAX_EVENTS[i]]?.status === "completed") idx = i;
    }
    return idx;
  };

  const linkStatus = (link) => {
    if (link.last_error) return "error";
    const events = link.current_snapshot?.events || {};
    if (events.DELIVERED?.status === "completed") return "delivered";
    // Has any completed event → pending (in transit)
    for (const e of CUBAMAX_EVENTS) {
      if (events[e]?.status === "completed") return "pending";
    }
    return "pending";
  };

  const filterCounts = React.useMemo(() => {
    const counts = { all: links.length, pending: 0, delivered: 0, vip: 0, error: 0 };
    for (const l of links) {
      const s = linkStatus(l);
      counts[s] = (counts[s] || 0) + 1;
      if (l.is_vip) counts.vip++;
    }
    return counts;
  }, [links]);

  const sortedLinks = React.useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    let filtered = q
      ? links.filter((l) => {
          const snap = l.current_snapshot || {};
          const hay = [
            l.label,
            l.url,
            snap.tracking_code,
            snap.hbl,
            snap.recipient,
            snap.customer_hint,
            l.last_error,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return hay.includes(q);
        })
      : links;
    if (statusFilter !== "all") {
      filtered = filtered.filter((l) => {
        if (statusFilter === "vip") return !!l.is_vip;
        return linkStatus(l) === statusFilter;
      });
    }
    const arr = [...filtered];
    const dir = sortDir === "asc" ? 1 : -1;
    const cmp = (a, b) => {
      // Primary sort: VIP always floats to the top regardless of column selected
      const vipA = a.is_vip ? 1 : 0;
      const vipB = b.is_vip ? 1 : 0;
      if (vipA !== vipB) return vipB - vipA;

      let va, vb;
      switch (sortKey) {
        case "label":
          va = (a.label || "").toLowerCase();
          vb = (b.label || "").toLowerCase();
          break;
        case "tracking":
          va = (a.current_snapshot?.tracking_code || "").toLowerCase();
          vb = (b.current_snapshot?.tracking_code || "").toLowerCase();
          break;
        case "recipient":
          va = (a.current_snapshot?.recipient || "").toLowerCase();
          vb = (b.current_snapshot?.recipient || "").toLowerCase();
          break;
        case "progress":
          va = latestCompletedIdx(a);
          vb = latestCompletedIdx(b);
          break;
        case "last_check":
          va = a.last_checked_at || "";
          vb = b.last_checked_at || "";
          break;
        case "added":
          va = a.created_at || "";
          vb = b.created_at || "";
          break;
        default:
          // Event column sort (e.g. "event:SHIPPED")
          if (sortKey && sortKey.startsWith("event:")) {
            const evName = sortKey.slice(6);
            const evA = a.current_snapshot?.events?.[evName];
            const evB = b.current_snapshot?.events?.[evName];
            va = evA?.status === "completed" ? 1 : evA?.status === "pending" ? 0 : -1;
            vb = evB?.status === "completed" ? 1 : evB?.status === "pending" ? 0 : -1;
          } else {
            va = a.created_at || "";
            vb = b.created_at || "";
          }
      }
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      // Tertiary sort: always newest-first by created_at
      const ca = a.created_at || "";
      const cb = b.created_at || "";
      if (ca > cb) return -1;
      if (ca < cb) return 1;
      return 0;
    };
    arr.sort(cmp);
    return arr;
  }, [links, sortKey, sortDir, searchQuery, statusFilter]);

  const eventCompletedCount = React.useMemo(() => {
    const counts = {};
    for (const ev of CUBAMAX_EVENTS) counts[ev] = 0;
    for (const link of links) {
      const events = link.current_snapshot?.events || {};
      for (const ev of CUBAMAX_EVENTS) {
        if (events[ev]?.status === "completed") counts[ev]++;
      }
    }
    return counts;
  }, [links]);

  const SortHeader = ({ label, sortId, className = "" }) => (
    <th
      className={`text-left p-2 font-mono uppercase text-[10px] tracking-wider cursor-pointer select-none hover:bg-zinc-200 transition-colors ${className}`}
      onClick={() => toggleSort(sortId)}
      data-testid={`sort-${sortId}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === sortId ? (
          sortDir === "asc" ? (
            <CaretUp size={10} weight="bold" className="text-[#002FA7]" />
          ) : (
            <CaretDown size={10} weight="bold" className="text-[#002FA7]" />
          )
        ) : (
          <span className="text-zinc-300 text-[8px]">↕</span>
        )}
      </span>
    </th>
  );

  return (
    <div data-testid="tracker-tab">
      <TrackerDiagnostics />
      {/* Header status + settings */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="border-2 border-zinc-950 p-4">
          <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mb-2">
            <Package size={12} weight="bold" className="inline mr-1" /> Tracked
          </div>
          <div className="font-mono text-4xl font-bold">{links.length}</div>
          <div className="text-xs text-zinc-500 mt-1">shipments monitored</div>
        </div>
        <div className="border-2 border-zinc-950 p-4">
          <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mb-2">
            <Clock size={12} weight="bold" className="inline mr-1" /> Last run
          </div>
          <div className="font-mono text-lg font-bold">
            {lastRun?.at ? timeAgo(lastRun.at) : "never"}
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            {lastRun?.summary
              ? `${lastRun.summary.checked} checked · ${lastRun.summary.with_changes} changes · ${lastRun.summary.emails_sent} emails`
              : "auto-runs cada 30 min"}
          </div>
        </div>
        <div className="border-2 border-zinc-950 p-4">
          <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mb-2">
            <Envelope size={12} weight="bold" className="inline mr-1" /> Email
          </div>
          <div className="font-mono text-sm truncate" title={settings?.notify_email}>
            {settings?.notify_email || "—"}
          </div>
          {settings && !settings.resend_configured && (
            <div className="text-xs text-[#FF2A00] mt-1 font-mono uppercase">
              <Warning size={10} weight="fill" className="inline mr-0.5" />
              Resend API key not configured
            </div>
          )}
        </div>
      </div>

      {/* Settings row */}
      <div className="border border-zinc-200 p-4 mb-6 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <label className="font-mono uppercase text-[10px] tracking-[0.2em] block mb-1">
            Notification email
          </label>
          <Input
            type="email"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            className="rounded-none font-mono text-sm"
            data-testid="tracker-email-input"
          />
        </div>
        <Button
          onClick={handleSaveEmail}
          data-testid="tracker-save-email-button"
          className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
        >
          Save
        </Button>
        <Button
          onClick={handleCheckNow}
          disabled={checking || links.length === 0}
          data-testid="tracker-check-now-button"
          className="rounded-none bg-[#002FA7] hover:bg-[#001a5c] text-white font-bold uppercase tracking-wider"
        >
          <ArrowClockwise
            size={16}
            weight="bold"
            className={`mr-2 ${checking ? "animate-spin" : ""}`}
          />
          {checking ? "Checking..." : "Check now"}
        </Button>
      </div>

      {/* Telegram push section */}
      <div className="border-2 border-[#0088cc] bg-[#e6f4fa] p-4 mb-6">
        <div className="flex items-center gap-2 mb-3">
          <PaperPlaneTilt size={20} weight="fill" className="text-[#0088cc]" />
          <h3 className="font-heading font-bold text-lg">Telegram Push</h3>
          {settings?.telegram_chat_id ? (
            <Badge className="rounded-none bg-green-100 text-green-800 border border-green-300 font-mono uppercase tracking-wider ml-auto">
              Conectado
            </Badge>
          ) : (
            <Badge className="rounded-none bg-amber-100 text-amber-800 border border-amber-300 font-mono uppercase tracking-wider ml-auto">
              Sin configurar
            </Badge>
          )}
        </div>

        {!settings?.telegram_chat_id ? (
          <div className="space-y-3 text-sm">
            <p className="text-zinc-700">
              <strong>Paso 1:</strong> Abre Telegram → busca tu bot →{" "}
              <a
                href="https://t.me/"
                target="_blank"
                rel="noreferrer"
                className="text-[#0088cc] underline"
              >
                envíale <code className="bg-white px-1 border border-zinc-300">/start</code>
              </a>
            </p>
            <p className="text-zinc-700">
              <strong>Paso 2:</strong> Click en <em>"Detectar chat"</em> abajo y elige tu chat de la lista.
            </p>
            <div className="flex flex-wrap gap-2 items-end">
              <Button
                onClick={handleDetectTelegram}
                disabled={detectingTelegram}
                data-testid="telegram-detect-button"
                className="rounded-none bg-[#0088cc] hover:bg-[#006ba6] text-white font-bold uppercase tracking-wider"
              >
                <ArrowClockwise
                  size={14}
                  weight="bold"
                  className={`mr-2 ${detectingTelegram ? "animate-spin" : ""}`}
                />
                Detectar chat
              </Button>
              <span className="text-xs text-zinc-500 font-mono uppercase tracking-wider">
                o pega tu chat_id manualmente:
              </span>
              <Input
                value={telegramChatId}
                onChange={(e) => setTelegramChatIdState(e.target.value)}
                placeholder="123456789"
                className="rounded-none w-40 font-mono text-sm"
                data-testid="telegram-chatid-input"
              />
              <Button
                onClick={handleSaveChatId}
                variant="outline"
                className="rounded-none border-2 border-zinc-950 font-bold uppercase tracking-wider"
                data-testid="telegram-save-button"
              >
                Guardar
              </Button>
            </div>
            {telegramChats.length > 0 && (
              <ul className="space-y-2 mt-3">
                {telegramChats.map((c) => (
                  <li
                    key={c.chat_id}
                    className="border border-[#0088cc] bg-white p-3 flex justify-between items-center gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-mono font-bold text-sm">
                        {c.first_name || c.title || c.username || c.chat_id}
                      </div>
                      <div className="font-mono text-[10px] text-zinc-500">
                        chat_id: {c.chat_id} · type: {c.type}
                      </div>
                      {c.last_message && (
                        <div className="text-xs text-zinc-600 italic mt-1">
                          "{c.last_message}"
                        </div>
                      )}
                    </div>
                    <Button
                      onClick={() => handlePickChat(c.chat_id)}
                      size="sm"
                      className="rounded-none bg-[#0088cc] hover:bg-[#006ba6] text-white font-bold uppercase tracking-wider"
                      data-testid={`telegram-pick-${c.chat_id}`}
                    >
                      Usar este
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]">
                <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mb-1">
                  Chat ID configurado
                </div>
                <div className="font-mono text-lg font-bold" data-testid="telegram-private-chat-id">
                  {settings.telegram_chat_id}
                </div>
                {settings.telegram_group_chat_id && (
                  <div className="font-mono text-xs text-zinc-500 mt-1" data-testid="telegram-group-chat-id">
                    + Grupo: <span className="font-bold text-zinc-800">{settings.telegram_group_chat_id}</span>
                  </div>
                )}
              </div>
              <Button
                onClick={handleTestTelegram}
                data-testid="telegram-test-button"
                className="rounded-none bg-[#0088cc] hover:bg-[#006ba6] text-white font-bold uppercase tracking-wider"
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
                Enviar prueba
              </Button>
              <Button
                onClick={() => handlePickChat("")}
                variant="outline"
                className="rounded-none border-2 border-red-500 text-red-600 hover:bg-red-500 hover:text-white font-bold uppercase tracking-wider"
                data-testid="telegram-clear-button"
              >
                Desconectar
              </Button>
            </div>
            <div className="border-t border-[#0088cc]/30 pt-3 flex flex-wrap gap-2 items-center">
              <div className="text-xs text-zinc-700 font-mono uppercase tracking-wider">
                📊 Reporte general de todos los tracking:
              </div>
              <Button
                onClick={() => handleSendReport(false)}
                disabled={sendingReport || links.length === 0}
                data-testid="telegram-report-button"
                className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider"
              >
                <PaperPlaneTilt size={14} weight="bold" className="mr-2" />
                Enviar reporte
              </Button>
              <Button
                onClick={() => handleSendReport(true)}
                disabled={sendingReport || links.length === 0}
                data-testid="telegram-force-report-button"
                className="rounded-none bg-[#FF2A00] hover:bg-[#c02100] text-white font-bold uppercase tracking-wider"
              >
                <ArrowClockwise
                  size={14}
                  weight="bold"
                  className={`mr-2 ${sendingReport ? "animate-spin" : ""}`}
                />
                {sendingReport ? "Procesando..." : "Forzar + reporte"}
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Add / upload */}
      <div className="border border-zinc-200 p-3 sm:p-4 mb-6 space-y-3">
        <div className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-700">
          Agregar tracking
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
          <Input
            type="url"
            value={newUrl}
            onChange={(e) => setNewUrl(e.target.value)}
            placeholder="Código CMT o URL de cubamax.com/es/tracking/..."
            className="rounded-none font-mono text-sm flex-1 sm:min-w-[280px] h-11 sm:h-10"
            data-testid="tracker-new-url-input"
          />
          <Input
            type="text"
            value={newLabel}
            onChange={(e) => setNewLabel(e.target.value)}
            placeholder="Nota (opcional)"
            className="rounded-none font-mono text-sm sm:w-52 h-11 sm:h-10"
            data-testid="tracker-new-label-input"
          />
          <div className="flex gap-2">
            <Button
              onClick={handleAddLink}
              disabled={!newUrl.trim()}
              data-testid="tracker-add-link-button"
              className="rounded-none bg-zinc-950 hover:bg-[#002FA7] text-white font-bold uppercase tracking-wider h-11 sm:h-10 flex-1 sm:flex-initial"
            >
              <Plus size={16} weight="bold" className="mr-1" /> Agregar
            </Button>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              data-testid="tracker-upload-button"
              className="rounded-none border-2 border-zinc-950 hover:bg-zinc-950 hover:text-white font-bold uppercase tracking-wider h-11 sm:h-10 flex-1 sm:flex-initial"
            >
              <FileArrowUp size={16} weight="bold" className="mr-1" /> Excel
            </Button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={handleUpload}
            data-testid="tracker-file-input"
          />
        </div>
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-400">
          Excel: se leen códigos CMT (o URLs) de la columna F. Etiquetas auto-generadas.
        </p>
      </div>

      {/* Filter chips */}
      <div className="mb-3 flex flex-wrap gap-2 overflow-x-auto sm:overflow-visible -mx-1 px-1">
        {[
          { key: "all", label: "Todos", color: "zinc" },
          { key: "vip", label: "⭐ VIP", color: "yellow" },
          { key: "pending", label: "En tránsito", color: "blue" },
          { key: "delivered", label: "Entregados", color: "green" },
          { key: "error", label: "Con error", color: "red" },
        ].map((f) => {
          const active = statusFilter === f.key;
          const count = filterCounts[f.key] ?? 0;
          const colorClasses = {
            zinc: active ? "bg-zinc-950 text-white border-zinc-950" : "bg-white border-zinc-300 text-zinc-700",
            yellow: active ? "bg-yellow-500 text-white border-yellow-500" : "bg-white border-yellow-400 text-yellow-700",
            blue: active ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-blue-300 text-blue-700",
            green: active ? "bg-green-600 text-white border-green-600" : "bg-white border-green-300 text-green-700",
            red: active ? "bg-red-600 text-white border-red-600" : "bg-white border-red-300 text-red-700",
          }[f.color];
          return (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`filter-chip-${f.key}`}
              className={`shrink-0 px-3 h-8 border-2 font-mono text-[11px] uppercase tracking-wider flex items-center gap-1 rounded-none transition-colors ${colorClasses}`}
            >
              {f.label}
              <span className={`ml-1 px-1.5 rounded-none text-[10px] ${active ? "bg-white/20" : "bg-zinc-100"}`}>
                {count}
              </span>
            </button>
          );
        })}
      </div>

      {/* Links table */}
      <div className="border border-zinc-200">
        <div className="bg-zinc-950 text-white px-3 py-2 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
          <span className="font-mono uppercase text-xs tracking-[0.2em] shrink-0">
            Envíos ({searchQuery ? `${sortedLinks.length}/${links.length}` : links.length})
          </span>
          <div className="flex items-center gap-2 flex-1 sm:max-w-md relative w-full">
            <MagnifyingGlass
              size={14}
              weight="bold"
              className="absolute left-2 text-zinc-400 pointer-events-none"
            />
            <Input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Buscar código, destinatario, nota..."
              className="rounded-none font-mono text-xs pl-7 pr-7 h-9 bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 w-full"
              data-testid="tracker-search-input"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                data-testid="tracker-search-clear"
                className="absolute right-2 text-zinc-400 hover:text-white"
                title="Limpiar búsqueda"
              >
                <XIcon size={14} weight="bold" />
              </button>
            )}
          </div>
          <Button
            onClick={() => { setSortKey("added"); setSortDir("desc"); }}
            size="sm"
            variant="outline"
            data-testid="tracker-sort-recent-button"
            title="Ordenar por más nuevo primero"
            className={`rounded-none h-9 uppercase text-[10px] tracking-wider shrink-0 ${
              sortKey === "added" && sortDir === "desc"
                ? "border-[#002FA7] bg-[#002FA7] text-white"
                : "border-zinc-500 text-zinc-300 hover:bg-zinc-800 hover:text-white"
            }`}
          >
            <Clock size={12} weight="bold" className="mr-1" />
            Recientes
          </Button>
          {selectedIds.size > 0 && (
            <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto" data-testid="tracker-bulk-bar">
              <span className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-300">
                {selectedIds.size} seleccionado{selectedIds.size > 1 ? "s" : ""}
              </span>
              <CmtExportButtons links={links} selectedIds={selectedIds} />
              <Button
                onClick={handleBulkDelete}
                size="sm"
                data-testid="tracker-bulk-delete-button"
                className="rounded-none bg-[#FF2A00] hover:bg-[#c02100] text-white font-bold uppercase text-[10px] tracking-wider h-7"
              >
                <Trash size={12} weight="bold" className="mr-1" />
                Borrar lote
              </Button>
              <Button
                onClick={() => setSelectedIds(new Set())}
                size="sm"
                variant="outline"
                className="rounded-none border-zinc-500 text-zinc-300 hover:bg-zinc-800 hover:text-white h-7 text-[10px] uppercase tracking-wider"
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>
        {loading && links.length === 0 ? (
          <div className="p-12 text-center font-mono uppercase text-xs tracking-[0.2em] text-zinc-400">
            Loading...
          </div>
        ) : links.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={48} weight="duotone" className="mx-auto mb-3 text-zinc-300" />
            <p className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-500">
              No tracked links yet. Add one or upload an Excel.
            </p>
          </div>
        ) : (
          <>
          {/* Mobile card view (< md) */}
          <div className="md:hidden divide-y divide-zinc-200" data-testid="tracked-links-cards">
            {sortedLinks.map((link) => {
              const { latest, next, events } = currentStep(link);
              const latestEv = latest ? events[latest] : null;
              const isSel = selectedIds.has(link.id);
              const isEditing = editingId === link.id;
              const doneCount = CUBAMAX_EVENTS.filter(
                (e) => events[e]?.status === "completed",
              ).length;
              return (
                <div
                  key={link.id}
                  className={`p-3 space-y-2 ${isSel ? "bg-blue-50" : link.is_vip ? "bg-yellow-50/40" : ""}`}
                  data-testid={`tracker-card-${link.id}`}
                >
                  {/* Row 1: checkbox + label + VIP star */}
                  <div className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={isSel}
                      onChange={() => toggleSelected(link.id)}
                      className="w-5 h-5 cursor-pointer accent-[#002FA7] mt-0.5 shrink-0"
                      data-testid={`tracker-select-m-${link.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="flex gap-1">
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(link.id);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            autoFocus
                            className="rounded-none h-9 text-sm font-mono"
                          />
                          <Button
                            size="sm"
                            onClick={() => saveEdit(link.id)}
                            className="h-9 w-9 p-0 rounded-none bg-green-600 hover:bg-green-700"
                          >
                            <CheckIcon size={14} weight="bold" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            className="h-9 w-9 p-0 rounded-none"
                          >
                            <XIcon size={14} weight="bold" />
                          </Button>
                        </div>
                      ) : (
                        <button
                          onClick={() => startEdit(link)}
                          className="flex items-center gap-1.5 text-left w-full"
                          data-testid={`tracker-label-m-${link.id}`}
                        >
                          {link.is_vip && (
                            <Star size={14} weight="fill" className="text-yellow-500 shrink-0" />
                          )}
                          <span className="font-bold text-sm truncate flex-1">
                            {link.label || "— sin nota —"}
                          </span>
                          <PencilSimple size={12} weight="bold" className="text-zinc-400 shrink-0" />
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => toggleVip(link)}
                      data-testid={`tracker-vip-toggle-m-${link.id}`}
                      className={`h-9 w-9 flex items-center justify-center border-2 rounded-none shrink-0 ${
                        link.is_vip
                          ? "border-yellow-500 bg-yellow-50 text-yellow-600"
                          : "border-zinc-300 text-zinc-400"
                      }`}
                      title={link.is_vip ? "Quitar VIP" : "Marcar VIP"}
                    >
                      <Star size={16} weight={link.is_vip ? "fill" : "bold"} />
                    </button>
                  </div>

                  {/* Row 2: tracking code (clickable → Cubamax) + recipient */}
                  <div className="flex justify-between items-baseline gap-2 pl-7">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noreferrer"
                      className="font-mono text-xs font-bold text-[#002FA7] underline decoration-dotted underline-offset-2 truncate flex items-center gap-1 active:opacity-70"
                      data-testid={`tracker-open-link-m-${link.id}`}
                      title="Abrir seguimiento en Cubamax"
                    >
                      {link.current_snapshot?.tracking_code || "—"}
                      <ArrowSquareOut size={11} weight="bold" className="shrink-0" />
                    </a>
                    <span className="font-mono text-[10px] text-zinc-500 truncate">
                      {link.current_snapshot?.recipient || "sin destinatario"}
                    </span>
                  </div>

                  {/* Row 3: current step */}
                  {latest && (
                    <div className="pl-7">
                      <div
                        className="text-[11px] font-mono uppercase tracking-wider text-[#002FA7] font-bold"
                        data-testid={`link-current-step-m-${link.id}`}
                      >
                        ➜ {latest}
                        {next && <span className="text-zinc-400"> → {next}</span>}
                      </div>
                      {latestEv?.description && (
                        <div className="text-[10px] text-zinc-600 italic mt-0.5 line-clamp-2">
                          "{latestEv.description}"
                        </div>
                      )}
                    </div>
                  )}

                  {/* Row 4: 6 event badges (progress dots) */}
                  <div className="flex gap-1 pl-7 items-center">
                    {CUBAMAX_EVENTS.map((e) => {
                      const ev = link.current_snapshot?.events?.[e];
                      const done = ev?.status === "completed";
                      return (
                        <div
                          key={e}
                          className={`h-2 flex-1 ${
                            done ? "bg-green-500" : ev ? "bg-zinc-300" : "bg-zinc-100"
                          }`}
                          title={`${e}: ${ev?.status || "n/a"}`}
                        />
                      );
                    })}
                    <span className="text-[10px] font-mono text-zinc-500 ml-1">
                      {doneCount}/6
                    </span>
                  </div>

                  {/* Row 5: meta + actions */}
                  <div className="flex justify-between items-center pl-7 pt-1">
                    <div className="text-[10px] font-mono text-zinc-500 truncate">
                      {timeAgo(link.last_checked_at) || "sin revisar"}
                      {link.last_error && (
                        <span
                          className="text-[#FF2A00] ml-1 truncate"
                          title={link.last_error}
                        >
                          · error
                        </span>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleCheckOne(link.id)}
                        disabled={checkingId === link.id}
                        data-testid={`tracker-check-one-m-${link.id}`}
                        className="h-9 w-9 flex items-center justify-center border-2 border-zinc-300 hover:border-[#002FA7] rounded-none"
                        title="Revisar solo este"
                      >
                        <ArrowClockwise
                          size={14}
                          weight="bold"
                          className={checkingId === link.id ? "animate-spin" : ""}
                        />
                      </button>
                      <button
                        onClick={() => openHistory(link)}
                        data-testid={`tracker-history-m-${link.id}`}
                        className="h-9 w-9 flex items-center justify-center border-2 border-zinc-300 rounded-none"
                        title="Ver historial"
                      >
                        <ClipboardText size={14} weight="bold" />
                      </button>
                      <button
                        onClick={() => handleDelete(link.id)}
                        data-testid={`tracker-delete-m-${link.id}`}
                        className="h-9 w-9 flex items-center justify-center border-2 border-red-300 text-red-600 rounded-none"
                        title="Borrar"
                      >
                        <Trash size={14} weight="bold" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table view (>= md) */}
          <div className="overflow-x-auto hidden md:block">
            <table className="w-full text-sm" data-testid="tracked-links-table">
              <thead className="bg-zinc-100">
                <tr>
                  <th className="p-2 w-8">
                    <input
                      type="checkbox"
                      checked={selectedIds.size === sortedLinks.length && sortedLinks.length > 0}
                      onChange={() => toggleAll(sortedLinks)}
                      className="cursor-pointer accent-[#002FA7]"
                      data-testid="tracker-select-all"
                    />
                  </th>
                  <SortHeader label="Label / URL" sortId="label" />
                  <SortHeader label="Tracking" sortId="tracking" />
                  <SortHeader label="Destinatario" sortId="recipient" />
                  <SortHeader label="Progreso" sortId="progress" />
                  {CUBAMAX_EVENTS.map((e) => {
                    const evSortId = `event:${e}`;
                    const isActive = sortKey === evSortId;
                    return (
                      <th
                        key={e}
                        className="text-left p-2 font-mono uppercase text-[9px] tracking-wider cursor-pointer select-none hover:bg-zinc-200 transition-colors"
                        onClick={() => toggleSort(evSortId)}
                        title={`${e} — ${eventCompletedCount[e]} completados`}
                        data-testid={`sort-event-${e}`}
                      >
                        <span className="inline-flex items-center gap-1">
                          {e.split(" ")[0].slice(0, 6)}
                          {isActive ? (
                            sortDir === "asc" ? (
                              <CaretUp size={9} weight="bold" className="text-[#002FA7]" />
                            ) : (
                              <CaretDown size={9} weight="bold" className="text-[#002FA7]" />
                            )
                          ) : (
                            <span className="text-zinc-300 text-[7px]">↕</span>
                          )}
                        </span>
                        <div className="text-[8px] text-zinc-400 normal-case font-normal mt-0.5">
                          {eventCompletedCount[e]}/{links.length}
                        </div>
                      </th>
                    );
                  })}
                  <SortHeader label="Last check" sortId="last_check" />
                  <th className="p-2"></th>
                </tr>
              </thead>
              <tbody>
                {sortedLinks.map((link) => {
                  const { latest, next, events } = currentStep(link);
                  const latestEv = latest ? events[latest] : null;
                  const isSel = selectedIds.has(link.id);
                  const isEditing = editingId === link.id;
                  return (
                  <tr
                    key={link.id}
                    className={`border-t border-zinc-100 align-top ${isSel ? "bg-blue-50" : ""}`}
                    data-testid={`tracked-link-row-${link.id}`}
                  >
                    <td className="p-2 w-8">
                      <input
                        type="checkbox"
                        checked={isSel}
                        onChange={() => toggleSelected(link.id)}
                        className="cursor-pointer accent-[#002FA7]"
                        data-testid={`tracker-select-${link.id}`}
                      />
                    </td>
                    <td className="p-2 max-w-[220px]">
                      {isEditing ? (
                        <div className="flex gap-1 items-center">
                          <Input
                            value={editLabel}
                            onChange={(e) => setEditLabel(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") saveEdit(link.id);
                              if (e.key === "Escape") cancelEdit();
                            }}
                            autoFocus
                            className="rounded-none h-7 font-mono text-xs"
                            data-testid={`tracker-edit-input-${link.id}`}
                          />
                          <Button
                            size="sm"
                            onClick={() => saveEdit(link.id)}
                            className="h-7 w-7 p-0 rounded-none bg-green-600 hover:bg-green-700"
                            data-testid={`tracker-edit-save-${link.id}`}
                          >
                            <CheckIcon size={12} weight="bold" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={cancelEdit}
                            className="h-7 w-7 p-0 rounded-none"
                          >
                            <XIcon size={12} weight="bold" />
                          </Button>
                        </div>
                      ) : (
                        <div
                          className="flex items-center gap-1 group cursor-pointer"
                          onClick={() => startEdit(link)}
                          data-testid={`tracker-label-${link.id}`}
                        >
                          {link.is_vip && (
                            <Star
                              size={12}
                              weight="fill"
                              className="text-yellow-500 shrink-0"
                              title="VIP — revisión cada 5 min"
                            />
                          )}
                          <div className="font-semibold text-xs truncate" title={link.label}>
                            {link.label || "— sin nota —"}
                          </div>
                          <PencilSimple
                            size={10}
                            weight="bold"
                            className="text-zinc-300 group-hover:text-[#002FA7] shrink-0"
                          />
                        </div>
                      )}
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer"
                        className="font-mono text-[10px] text-zinc-500 hover:text-[#002FA7] truncate block"
                        title={link.url}
                      >
                        {link.url}
                      </a>
                      {latest && (
                        <div
                          className="mt-1 text-[10px] font-mono uppercase tracking-wider text-[#002FA7] truncate"
                          title={latestEv?.description || ""}
                          data-testid={`link-current-step-${link.id}`}
                        >
                          ➜ {latest}
                          {next && <span className="text-zinc-400"> → {next}</span>}
                        </div>
                      )}
                      {latestEv?.description && (
                        <div
                          className="text-[10px] text-zinc-600 italic mt-0.5 line-clamp-2"
                          title={latestEv.description}
                        >
                          "{latestEv.description}"
                        </div>
                      )}
                    </td>
                    <td className="p-2 font-mono text-xs">
                      <div className="font-bold">
                        {link.current_snapshot?.tracking_code || "—"}
                      </div>
                    </td>
                    <td className="p-2 font-mono text-xs">
                      <div className="text-zinc-700">
                        {link.current_snapshot?.recipient?.slice(0, 30) || "—"}
                      </div>
                    </td>
                    <td className="p-2 font-mono text-[10px] text-zinc-600 whitespace-nowrap">
                      {latest ? (
                        <span className="uppercase tracking-wider font-bold text-[#002FA7]">
                          {latest.split(" ")[0]}
                        </span>
                      ) : (
                        <span className="text-zinc-300">—</span>
                      )}
                    </td>
                    {CUBAMAX_EVENTS.map((e) => (
                      <td key={e} className="p-2">
                        {renderEventBadge(link, e)}
                      </td>
                    ))}
                    <td className="p-2 font-mono text-[10px] text-zinc-500">
                      {timeAgo(link.last_checked_at)}
                      {link.last_error && (
                        <div className="text-[#FF2A00] font-mono truncate max-w-[130px]" title={link.last_error}>
                          {link.last_error}
                        </div>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1 justify-end">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => toggleVip(link)}
                          data-testid={`tracker-vip-toggle-${link.id}`}
                          className={`rounded-none ${
                            link.is_vip
                              ? "border-yellow-500 bg-yellow-50 text-yellow-600 hover:bg-yellow-100"
                              : "border-zinc-300 text-zinc-400 hover:border-yellow-500 hover:text-yellow-500"
                          }`}
                          title={link.is_vip ? "Quitar VIP (revisión cada 30 min)" : "Marcar VIP (revisión cada 5 min)"}
                        >
                          <Star
                            size={12}
                            weight={link.is_vip ? "fill" : "bold"}
                          />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCheckOne(link.id)}
                          disabled={checkingId === link.id}
                          data-testid={`tracker-check-one-${link.id}`}
                          className="rounded-none border-zinc-300 hover:border-[#002FA7] hover:text-[#002FA7]"
                          title="Revisar solo este"
                        >
                          <ArrowClockwise
                            size={12}
                            weight="bold"
                            className={checkingId === link.id ? "animate-spin" : ""}
                          />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openHistory(link)}
                          data-testid={`tracker-history-${link.id}`}
                          className="rounded-none border-zinc-300"
                          title="View history"
                        >
                          <ClipboardText size={12} weight="bold" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDelete(link.id)}
                          data-testid={`tracker-delete-${link.id}`}
                          className="rounded-none border-red-300 text-red-600 hover:bg-red-50"
                        >
                          <Trash size={12} weight="bold" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>

      {/* Global recent changes log */}
      <div className="mt-8">
        <div className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-700 mb-3">
          Recent change log
        </div>
        {history.length === 0 ? (
          <div className="border border-zinc-200 p-6 text-center font-mono text-xs uppercase tracking-wider text-zinc-400">
            No changes detected yet. Bot runs cada 30 min.
          </div>
        ) : (
          <div className="space-y-2" data-testid="tracker-recent-changes">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="border border-zinc-200 p-3 flex gap-3 items-start">
                <div className="flex-shrink-0">
                  {h.email_sent ? (
                    <CheckCircle size={20} weight="fill" className="text-green-600" />
                  ) : (
                    <Envelope size={20} weight="regular" className="text-zinc-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{h.label || h.url}</div>
                  <div className="font-mono text-[10px] text-zinc-500 mb-1">
                    {new Date(h.created_at).toLocaleString()} · {h.email_sent ? "email sent" : "email pending"}
                  </div>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {h.changes.map((c, i) => (
                      <span
                        key={i}
                        className="font-mono text-[10px] uppercase tracking-wider bg-green-100 text-green-800 border border-green-300 px-1.5 py-0.5"
                      >
                        {c.event}: {c.from} → {c.to}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* History modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="rounded-none border-2 border-zinc-950 max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-xl tracking-tight">
              Change history
            </DialogTitle>
            <DialogDescription className="font-mono uppercase text-[10px] tracking-[0.2em]">
              {selectedLink?.label || selectedLink?.url}
            </DialogDescription>
          </DialogHeader>
          {/* Current full timeline */}
          {selectedLink?.current_snapshot?.timeline?.length > 0 && (
            <div className="border border-zinc-200 p-3 mb-3 bg-zinc-50">
              <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-500 mb-2">
                📋 Camino paso a paso ({selectedLink.current_snapshot.timeline.length} eventos)
              </div>
              <ol className="space-y-2">
                {selectedLink.current_snapshot.timeline.map((t, i) => (
                  <li key={i} className="font-mono text-xs flex gap-2" data-testid={`timeline-item-${i}`}>
                    <span className={`shrink-0 ${t.status === "completed" ? "text-green-600" : "text-zinc-400"}`}>
                      {t.status === "completed" ? "✅" : "⏳"}
                    </span>
                    <div className="flex-1">
                      <div className="flex flex-wrap gap-2">
                        <span className="font-bold text-[#002FA7]">{t.label || t.raw_name}</span>
                        {t.date && <span className="text-zinc-500">· {t.date}</span>}
                      </div>
                      {t.description && (
                        <div className="text-zinc-600 italic mt-0.5">"{t.description}"</div>
                      )}
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          )}
          <div className="font-mono uppercase text-[10px] tracking-[0.2em] text-zinc-700 mb-2">
            Cambios detectados por el bot
          </div>
          {history.length === 0 ? (
            <p className="font-mono uppercase text-xs tracking-[0.2em] text-zinc-400 py-8 text-center">
              No changes yet.
            </p>
          ) : (
            <ul className="space-y-3">
              {history.map((h) => (
                <li key={h.id} className="border border-zinc-200 p-3">
                  <div className="font-mono text-xs text-zinc-500 mb-2 flex justify-between">
                    <span>{new Date(h.created_at).toLocaleString()}</span>
                    <Badge
                      className={`rounded-none font-mono uppercase tracking-wider ${
                        h.email_sent
                          ? "bg-green-100 text-green-800 border border-green-300"
                          : "bg-zinc-100 text-zinc-500 border border-zinc-300"
                      }`}
                    >
                      {h.email_sent ? "email sent" : "no email"}
                    </Badge>
                  </div>
                  <ul className="space-y-1">
                    {h.changes.map((c, i) => (
                      <li key={i} className="font-mono text-xs">
                        <div className="flex flex-wrap gap-2">
                          <span className="font-bold text-[#002FA7]">{c.event}</span>
                          <span className="text-zinc-500">{c.from}</span>
                          <span className="text-green-600">→ {c.to}</span>
                          {c.date && <span className="text-zinc-400">· {c.date}</span>}
                        </div>
                        {c.description && (
                          <div className="text-zinc-600 italic pl-2 mt-0.5">
                            "{c.description}"
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)} className="rounded-none">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mobile sticky bottom toolbar */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-zinc-950 border-t-2 border-[#002FA7] shadow-lg"
        data-testid="mobile-bottom-toolbar"
      >
        <div className="flex divide-x divide-zinc-800">
          <button
            onClick={() => {
              searchInputRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => searchInputRef.current?.focus(), 400);
            }}
            data-testid="mobile-toolbar-search"
            className="flex-1 h-14 flex flex-col items-center justify-center gap-0.5 text-white active:bg-zinc-800"
          >
            <MagnifyingGlass size={18} weight="bold" />
            <span className="font-mono uppercase text-[9px] tracking-wider">Buscar</span>
          </button>
          <button
            onClick={() => {
              setSortKey("added");
              setSortDir("desc");
              setStatusFilter("all");
              window.scrollTo({ top: 0, behavior: "smooth" });
              toast.success("Ordenado por más recientes");
            }}
            data-testid="mobile-toolbar-recent"
            className="flex-1 h-14 flex flex-col items-center justify-center gap-0.5 text-white active:bg-zinc-800"
          >
            <Clock size={18} weight="bold" />
            <span className="font-mono uppercase text-[9px] tracking-wider">Recientes</span>
          </button>
          <button
            onClick={() => handleSendReport(false)}
            disabled={sendingReport || links.length === 0}
            data-testid="mobile-toolbar-report"
            className="flex-1 h-14 flex flex-col items-center justify-center gap-0.5 text-white active:bg-zinc-800 disabled:opacity-50 disabled:pointer-events-none"
          >
            <PaperPlaneTilt
              size={18}
              weight="bold"
              className={sendingReport ? "animate-pulse" : ""}
            />
            <span className="font-mono uppercase text-[9px] tracking-wider">Reporte</span>
          </button>
        </div>
      </div>
      {/* Spacer for mobile bottom bar */}
      <div className="md:hidden h-16" aria-hidden="true" />
    </div>
  );
};
